import type { ReasonNode, Session } from '../core/types'
import { kindLabelOf } from '../core/types'
import { endingLabelOf, stoodAgainst } from '../core/endings'
import { OBJECTION_STOOD_BELOW, STATUS_WORD } from '../core/vocabulary'
import { priorArtWord, readingWord, retractedWord, standsOnWord } from '../core/statusWords'
import { refutedUnder } from '../core/taint'

/**
 * The graph as one tree, for the keyboard and the screen reader.
 *
 * The list this replaces was every visible node in arrival order, one button
 * each: flat, so nothing said which claim a claim was a reply to, and one tab
 * stop per node, so a keyboard reached the zoom controls on its 29th press. The
 * tree is the same nodes in the shape the canvas draws them — each under its
 * parent, siblings in arrival order — carrying the facts a sighted reader gets
 * from a glance at a dot: what kind of claim it is, and what the record says
 * about it.
 *
 * Pure: the component in `NodeTree.tsx` renders these rows and owns focus.
 */

/** One row of the tree, in reading order (depth first). */
export interface TreeRow {
  id: string
  parentId: string | null
  /** 1 for the seed. */
  level: number
  /** Siblings including this one, and this one's place among them (1-based). */
  setSize: number
  posInSet: number
  /**
   * `true` when its children are on the canvas, `false` when it is folded with
   * something behind the fold, `null` for a leaf. The tree's expansion IS the
   * canvas fold — two different ideas of "open" for one branch would have the
   * tree and the map disagree about what exists.
   */
  expanded: boolean | null
  /** How many marks one unfold reveals, for a folded row; 0 otherwise. */
  folded: number
}

/**
 * The visible graph as tree rows. `visible` is the canvas's own visibility —
 * filters, playhead and folds applied — so the tree never names a node the map
 * is not drawing. Visibility is subtree-closed (`computeVisible` hides a hidden
 * node's whole branch), so every row's parent is a row.
 */
export function treeRows(
  session: Session,
  visible: ReadonlySet<string>,
  childrenOf: ReadonlyMap<string, readonly string[]>,
  folded: ReadonlyMap<string, number>,
): TreeRow[] {
  const root = session.order.find((id) => session.nodes[id]?.parentId === null && visible.has(id))
  if (!root) return []
  const rows: TreeRow[] = []
  const kidsOf = (id: string) => (childrenOf.get(id) ?? []).filter((c) => visible.has(c))
  const seen = new Set<string>()
  const walk = (id: string, parentId: string | null, level: number, pos: number, size: number) => {
    if (seen.has(id)) return
    seen.add(id)
    const kids = kidsOf(id)
    const behind = folded.get(id) ?? 0
    const expanded = folded.has(id) ? (behind > 0 ? false : null) : kids.length > 0 ? true : null
    rows.push({
      id,
      parentId,
      level,
      setSize: size,
      posInSet: pos,
      expanded,
      folded: folded.has(id) ? behind : 0,
    })
    kids.forEach((k, i) => walk(k, id, level + 1, i + 1, kids.length))
  }
  walk(root, null, 1, 1, 1)
  return rows
}

/**
 * What the record says about a node, in the words the card uses, or null.
 *
 * The reading label when a source check has run (said to predate a rewrite
 * when it does, because a check of other words is not a check of these), and
 * the ending when the path has closed — both, when both are true. A claim with
 * neither says "not checked yet", the card's words for the same record (it said
 * "not checked" while the card said this): in a tool whose whole job is telling
 * checked from argued, the absence is the status, and silence would read as
 * nothing to say. The seed is a question, not a claim, so it has a survey or
 * nothing. Every word is the status table's, lower case inside a name.
 *
 * With the session, two more, as the card says them: a withdrawn work its live
 * check cites, and the refuted claim it was reasoned from and not re-derived
 * since, named — a sighted reader sees that strand detach on the canvas, and a
 * tree that said only "not checked yet" there said less than the picture. And
 * the notch (`canvas/notch.ts`): an objection below it stood, in the card's
 * words, where a sighted reader sees the disc cut. Not on the seed, which is
 * not notched.
 *
 * A conjecture's check is its prior-art search, so it says the search's reading
 * where another claim says its source check's — or that no search has read it —
 * and never "not checked yet" (`priorArtWord`, D289).
 */
export function nodeStatus(n: ReasonNode, session?: Session): string | null {
  const parts: string[] = []
  const reading = readingWord(n)
  if (reading) parts.push(reading)
  const searched = priorArtWord(n)
  if (searched) parts.push(searched)
  const retracted = retractedWord(n)
  if (retracted) parts.push(retracted)
  const ending = endingLabelOf(n)
  if (ending) parts.push(ending)
  if (!parts.length) {
    if (n.parentId === null) return n.survey ? STATUS_WORD.fieldSurveyed.toLowerCase() : null
    parts.push(STATUS_WORD.notChecked.toLowerCase())
  }
  const refuted = session ? refutedUnder(session, n.id) : null
  if (refuted) parts.push(standsOnWord(refuted))
  if (session && stoodAgainst(session).has(n.id)) parts.push(OBJECTION_STOOD_BELOW)
  return parts.join(', ')
}

/** The row's accessible name: label, kind, status, and what a fold hides. The
 *  kind as the card names it (`kindLabelOf`), so a conjecture's carried
 *  expectation is not read out as a reported result. */
export function treeItemName(
  n: ReasonNode,
  row: Pick<TreeRow, 'folded'>,
  session?: Session,
): string {
  const status = nodeStatus(n, session)
  const fold = row.folded > 0 ? `, ${row.folded} folded inside` : ''
  return `${n.label}, ${kindLabelOf(n)}${status ? `, ${status}` : ''}${fold}`
}
